import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import Form from "antd/es/form";
import Radio from "antd/es/radio";
import Space from "antd/es/space";
import capitalize from "lodash-es/capitalize";
import { useIntl } from "react-intl";

import MarkerIcon from "@mapmycustomers/shared/enum/MarkerIcon";
import MarkerSize from "@mapmycustomers/shared/enum/MarkerSize";
import Organization from "@mapmycustomers/shared/types/Organization";
import Setting from "@mapmycustomers/shared/types/Setting";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";

import RadioGroup from "@app/component/input/RadioGroup";
import settingsAnalytics from "@app/scene/settings/settingsAnaytics";
import { getMe, getOrganization, getUserSettings } from "@app/store/iam";
import { updateSetting } from "@app/store/iam/actions";
import { RootState } from "@app/store/rootReducer";
import Iam from "@app/types/Iam";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";

import showSaveSettingSuccessfulMessage from "../../util/showSaveSettingSuccessfulMessage";
import SettingPage from "../common/SettingPage";

import PinStyleCards from "./component/PinStyleCards";

interface MapPreferencesForm {
  markerIcon: MarkerIcon;
  markerSize: MarkerSize;
}

interface Props {
  me: Iam;
  organization: Organization;
  settings: Setting[];
  updateSetting: typeof updateSetting.request;
}

const MapPreferences: React.FC<Props> = ({ me, organization, settings, updateSetting }) => {
  const intl = useIntl();
  const [form] = Form.useForm<MapPreferencesForm>();
  const [isDirty, setDirty, unsetDirty] = useBoolean();
  const [updating, setUpdating, unsetUpdating] = useBoolean();
  const [markerIcon, markerSize]: [Setting, Setting] = useMemo(
    () => [
      settings.find(({ key }) => key === "markerIcon")!,
      settings.find(({ key }) => key === "markerSize")!,
    ],
    [settings]
  );

  const mapPreferenceAnalytics = useExtendedAnalytics(["Map Preferences"], settingsAnalytics);

  const handleSubmit = useCallback(async () => {
    setUpdating();
    const values = form.getFieldsValue();
    const getUpdatingPromise = (setting: Setting): Promise<void> =>
      new Promise((resolve) =>
        updateSetting({ callback: () => resolve(), orgId: organization.id, setting, userId: me.id })
      );
    if (values.markerIcon !== markerIcon.value) {
      await getUpdatingPromise({ ...markerIcon, value: values.markerIcon });
    }
    if (values.markerSize !== markerSize.value) {
      await getUpdatingPromise({ ...markerSize, value: values.markerSize });
    }
    showSaveSettingSuccessfulMessage(intl);
    unsetUpdating();
    unsetDirty();
  }, [
    form,
    intl,
    markerIcon,
    markerSize,
    me,
    organization,
    setUpdating,
    unsetDirty,
    unsetUpdating,
    updateSetting,
  ]);

  const handleUnDo = useCallback(() => {
    form.resetFields();
    unsetDirty();
  }, [form, unsetDirty]);

  const handleSizeChangeAnalytics = useCallback(
    (key) => {
      mapPreferenceAnalytics.clicked([`Pin Size ${capitalize(key)}`]);
    },
    [mapPreferenceAnalytics]
  );

  return (
    <SettingPage
      changesSaving={updating}
      hasChanges={isDirty}
      onSaveChanges={form.submit}
      onUnDo={handleUnDo}
      title={intl.formatMessage({
        id: "settings.mapPreferences.title",
        defaultMessage: "Map Preferences",
        description: "Title of settings Map Preferences page",
      })}
    >
      <div>
        <Form
          form={form}
          initialValues={{
            markerIcon: markerIcon.value,
            markerSize: markerSize.value,
          }}
          onFieldsChange={() => setDirty()}
          onFinish={handleSubmit}
        >
          <div>
            <p>
              {intl.formatMessage({
                id: "settings.mapPreferences.pinStyle.label",
                defaultMessage: "Select pin style",
                description: "Title of Pin Style settings Map Preferences ",
              })}
            </p>
            <Form.Item name="markerIcon">
              <PinStyleCards analyticIssuer={mapPreferenceAnalytics} />
            </Form.Item>
            <p>
              {intl.formatMessage({
                id: "settings.mapPreferences.pinSize.label",
                defaultMessage: "Select pin size",
                description: "Title of Pin size settings Map Preferences ",
              })}
            </p>
            <Form.Item name="markerSize">
              <RadioGroup onChange={handleSizeChangeAnalytics}>
                <Space direction="vertical">
                  <Radio key={MarkerSize.SMALL} value={MarkerSize.SMALL}>
                    {intl.formatMessage({
                      id: "settings.mapPreferences.pinSize.small",
                      defaultMessage: "Small",
                      description: "Title of settings Map Preferences small marker",
                    })}
                  </Radio>
                  <Radio key={MarkerSize.LARGE} value={MarkerSize.LARGE}>
                    {intl.formatMessage({
                      id: "settings.mapPreferences.pinSize.large",
                      defaultMessage: "Large",
                      description: "Title of settings Map Preferences Large marker",
                    })}
                  </Radio>
                </Space>
              </RadioGroup>
            </Form.Item>
          </div>
        </Form>
      </div>
    </SettingPage>
  );
};

const mapStateToProps = (state: RootState) => ({
  me: getMe(state)!,
  organization: getOrganization(state)!,
  settings: getUserSettings(state)!,
});

const mapDispatchToProps = {
  updateSetting: updateSetting.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(MapPreferences);
